/* Each card owns its timer; only visible cards rotate. Manual browsing pauses it. */
if (!customElements.get('bc-set-carousel')) {
  customElements.define('bc-set-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('.bc-set-track');
      this.slides = [...this.querySelectorAll('.bc-set-slide')];
      if (!this.track || !this.slides.length) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.index = 0;
      this.visible = false;
      this.hovered = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.classList.add('is-ready');
      this.querySelector('.bc-set-controls').hidden = this.slides.length < 2;
      this.update();
      this.track.addEventListener('pointerdown', e => {
        this.gesture = {x:e.clientX, y:e.clientY, scroll:this.track.scrollLeft};
        this.dragged = false;
        this.pause();
      }, {signal, passive:true});
      this.track.addEventListener('pointermove', e => {
        if (this.gesture && Math.hypot(e.clientX-this.gesture.x,e.clientY-this.gesture.y)>10) this.dragged = true;
      }, {signal, passive:true});
      this.track.addEventListener('pointercancel', () => {this.dragged = true;}, {signal});
      this.track.addEventListener('click', e => {
        if (this.dragged || (this.gesture && Math.abs(this.track.scrollLeft-this.gesture.scroll)>10)) return;
        this.openPreview(e.detail > 0);
      }, {signal});
      this.track.addEventListener('wheel', e => { if (Math.abs(e.deltaX) > 0) this.pause(); }, {signal, passive: true});
      this.track.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openPreview(false); return; }
        const moves = {ArrowRight: this.index + 1, ArrowLeft: this.index - 1, Home: 0, End: this.slides.length - 1};
        if (!(e.key in moves)) return;
        e.preventDefault(); this.pause(); this.go(moves[e.key], false);
      }, {signal});
      this.track.addEventListener('scroll', () => {
        this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth))));
        this.update();
        this.loadNeighbors();
      }, {signal, passive: true});
      this.addEventListener('click', e => {
        const button = e.target.closest('[data-gallery]');
        if (!button) return;
        const action = button.dataset.gallery;
        this.pause(); this.go(this.index + (action === 'next' ? 1 : -1), false);
      }, {signal});
      this.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') { this.hovered = true; this.schedule(); } }, {signal});
      this.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') { this.hovered = false; this.schedule(); } }, {signal});
      this.addEventListener('focusin', () => this.pause(), {signal});
      document.addEventListener('visibilitychange', () => this.schedule(), {signal});
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.pause(); }, {signal});
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.25;
        if (this.visible) this.loadNeighbors();
        this.schedule();
      }, {threshold: 0.25});
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => {
        this.track.scrollTo({left: this.index * this.track.clientWidth, behavior: 'instant'});
      });
      this.resize.observe(this.track);
    }
    openPreview(pointerOpened) {
      this.pause();
      if (!this.dialog) {
        this.dialog = document.createElement('dialog');
        this.dialog.className = 'bc-set-preview-dialog';
        this.dialog.setAttribute('aria-label', this.getAttribute('aria-label'));
        this.dialog.innerHTML = '<div class="bc-set-preview-header"><h2></h2><button type="button" data-set-close aria-label="Close coaster preview" autofocus>×</button></div><div class="bc-set-preview-sides" role="group" aria-label="Coaster side" hidden><button type="button" data-set-side="front" aria-pressed="true">Front</button><button type="button" data-set-side="back" aria-pressed="false">Back</button></div><div class="bc-set-preview-art" aria-live="polite"></div>';
        this.append(this.dialog);
        const signal = this.abort.signal;
        this.dialog.querySelector('.bc-set-preview-sides').addEventListener('click', e => {
          const button = e.target.closest('[data-set-side]');
          if(button) this.showPreviewSide(button.dataset.setSide);
        }, {signal});
        this.dialog.querySelector('[data-set-close]').addEventListener('click', () => this.dialog.close(), {signal});
        this.dialog.addEventListener('keydown', () => {this.pointerOpened = false;}, {signal});
        this.dialog.addEventListener('click', e => {
          if(e.target !== this.dialog) return;
          const r = this.dialog.getBoundingClientRect();
          if(e.clientX<r.left || e.clientX>r.right || e.clientY<r.top || e.clientY>r.bottom) this.dialog.close();
        }, {signal});
        this.dialog.addEventListener('close', () => {
          if(this.pointerOpened) this.track.blur();
          else this.track.focus({preventScroll:true});
        }, {signal});
      }
      this.pointerOpened = pointerOpened;
      this.dialog.querySelector('.bc-set-preview-sides').hidden = !this.slides[this.index].dataset.backSrc;
      this.showPreviewSide('front');
      this.dialog.showModal();
    }
    showPreviewSide(side) {
      const source = this.slides[this.index];
      if(side === 'back' && !source.dataset.backSrc) return;
      const title = source.querySelector('img')?.alt || this.getAttribute('aria-label');
      const artwork = source.cloneNode(true);
      artwork.removeAttribute('aria-hidden');
      artwork.removeAttribute('data-back-src');
      if(side === 'back') {
        const image = document.createElement('img');
        image.src = source.dataset.backSrc;
        image.alt = `${title} — Back`;
        image.loading = 'eager';
        artwork.replaceChildren(image);
      } else {
        artwork.querySelectorAll('img').forEach(img => {
          if(img.dataset.src) {img.src = img.dataset.src; delete img.dataset.src;}
          img.loading = 'eager';
        });
      }
      this.dialog.querySelectorAll('[data-set-side]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.setSide === side)));
      this.dialog.querySelector('h2').textContent = title;
      this.dialog.querySelector('.bc-set-preview-art').replaceChildren(artwork);
    }

    pause() { this.paused = true; this.update(); this.schedule(); }
    loadNeighbors() {
      for (const offset of [0, 1, -1]) {
        const slide = this.slides[(this.index + offset + this.slides.length) % this.slides.length];
        const img = slide.querySelector('img');
        if (img?.dataset.src) { img.src = img.dataset.src; delete img.dataset.src; }
      }
    }
    go(index, automatic) {
      const next = (index + this.slides.length) % this.slides.length;
      const wraps = Math.abs(next - this.index) > 1;
      this.index = next;
      this.loadNeighbors();
      this.track.scrollTo({left: next * this.track.clientWidth, behavior: automatic && !wraps && !this.motion.matches ? 'smooth' : 'instant'});
      this.update();
    }
    update() {
      this.querySelector('[data-gallery-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
      this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== this.index)));
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.slides.length < 2 || this.paused || !this.visible || this.hovered || document.hidden) return;
      this.timer = setTimeout(() => { this.go(this.index + 1, true); this.schedule(); }, 5000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.dialog?.remove();
      this.dialog = null;
      this.abort?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.abort = null;
    }
  });
}
